import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  ReferenceArea,
  Legend,
} from 'recharts';
import { useAppSelector } from 'store/hook';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { ThemePalette } from 'mui.theme';
import type {
  MLInsight,
  TpmsEvidenceResponse,
  TpmsDataPoint,
} from 'types/ml-insights.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface SupportingEvidenceTpmsProps {
  data: MLInsight;
  evidence: TpmsEvidenceResponse | null;
  enableDarkTheme: boolean;
  onPointClick?: () => void;
  showDetailChart?: boolean;
  onToggleDetailChart?: () => void;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: TpmsDataPoint;
  }>;
  enableDarkTheme: boolean;
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes;
  timezone: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({
  active,
  payload,
  enableDarkTheme,
  preferredTimeDisplayMode,
  timezone,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const firstPayload = payload[0];
    const sampleTimestamp = firstPayload.payload?.sample_timestamp ?? '';

    const convertedTime = convertUtcToDisplayTime(
      sampleTimestamp,
      preferredTimeDisplayMode,
      timezone
    );
    return (
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.tableHeader
            : palette.toolBarBackground,
          padding: '10px',
          borderRadius: '5px',
          minWidth: '200px',
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
          {convertedTime}
        </Typography>
        {payload.map((entry, index) => (
          <Typography key={index} sx={{ fontSize: '12px', color: entry.color }}>
            {entry.name}: {entry.value?.toFixed(2)}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export const SupportingEvidenceTpms: React.FC<SupportingEvidenceTpmsProps> = ({
  data,
  evidence,
  enableDarkTheme,
  onPointClick,
  showDetailChart,
  onToggleDetailChart,
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { isEvidenceLoading } = useAppSelector(
    (state) => state.mlInsightsReducer
  );

  const { timezone, preferredTimeDisplayMode } = useTimeDisplayMode();
  const [isChartRendering, setIsChartRendering] = useState(true);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const chartData =
    (evidence?.evidenceValues as unknown as TpmsDataPoint[]) ?? [];
  const alertStartTs = data.alertStartTs ?? data.timestamp ?? '';

  const PRESSURE_COLOR = '#2196F3'; // Blue
  const FORECAST_COLOR = '#F59E0B'; // Amber/Orange

  const processedChartData = chartData.map((item: TpmsDataPoint) => ({
    ...item,
    ts: new Date(item.sample_timestamp).getTime(),
    originalTimestamp: item.sample_timestamp,
  }));

  useEffect(() => {
    if (isEvidenceLoading) {
      setIsChartRendering(true);
      return;
    }

    if (chartData.length === 0) {
      setIsChartRendering(false);
      return;
    }
    setIsChartRendering(true);
    const checkChartRendered = () => {
      if (chartContainerRef.current) {
        const svgElement = chartContainerRef.current.querySelector('svg');
        if (svgElement && svgElement.children.length > 0) {
          setTimeout(() => {
            setIsChartRendering(false);
          }, 100);
          return true;
        }
      }
      return false;
    };

    if (!checkChartRendered()) {
      const interval = setInterval(() => {
        if (checkChartRendered()) {
          clearInterval(interval);
        }
      }, 50);

      const timeout = setTimeout(() => {
        clearInterval(interval);
        setIsChartRendering(false);
      }, 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [isEvidenceLoading, chartData.length]);

  if (!chartData || (chartData.length === 0 && !isEvidenceLoading)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Typography>{t('no_supporting_evidence_available')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '540px',
        backgroundColor: palette.boxBackground,
        borderRadius: '8px',
        border: `1px solid ${
          enableDarkTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
        }`,
        boxShadow: enableDarkTheme
          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'scroll',
      }}
    >
      {isEvidenceLoading || isChartRendering ? (
        <Box sx={{ padding: '5%', height: '100%', marginTop: '20%' }}>
          <SingleFadeLoader isFullScreen={false} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px 12px 20px',
              borderBottom: `1px solid ${
                enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '700',
                color: palette.textPrimary,
              }}
            >
              {t('supporting_evidence')}
            </Typography>

            {onToggleDetailChart && (
              <IconButton
                onClick={onToggleDetailChart}
                sx={{
                  padding: '4px',
                  color: palette.textPrimary,
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  transform: showDetailChart
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
                title={
                  showDetailChart ? 'Hide detail chart' : 'Show detail chart'
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </IconButton>
            )}
          </Box>

          <Box
            ref={chartContainerRef}
            sx={{
              padding: '20px',
              height: 'calc(100% - 65px)',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={processedChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={
                    enableDarkTheme
                      ? palette.cartesianGrid
                      : palette.cartesianGrid
                  }
                />
                <ReferenceArea
                  x1={(() => {
                    const dataTimestamp = new Date(alertStartTs).getTime();
                    const hoursInMs = 3 * 60 * 60 * 1000;
                    return dataTimestamp - hoursInMs;
                  })()}
                  x2={(() => {
                    const dataTimestamp = new Date(alertStartTs).getTime();
                    const hoursInMs = 3 * 60 * 60 * 1000;
                    return dataTimestamp + hoursInMs;
                  })()}
                  fill="rgba(245, 158, 11, 0.1)"
                  fillOpacity={0.3}
                  stroke="none"
                />
                <XAxis
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(value) => {
                    const dataPoint = processedChartData.find(
                      (item) => item.ts === value
                    );
                    if (dataPoint?.originalTimestamp) {
                      return convertUtcToDisplayTime(
                        dataPoint.originalTimestamp,
                        preferredTimeDisplayMode,
                        timezone,
                        'MM-DD HH:mm'
                      );
                    }
                    return convertUtcToDisplayTime(
                      value,
                      preferredTimeDisplayMode,
                      timezone,
                      'MM-DD HH:mm'
                    );
                  }}
                  tick={{ fontSize: 10 }}
                  interval={100}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  label={{
                    value: 'Pressure',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: '10px' },
                  }}
                />
                <Tooltip
                  content={
                    <CustomTooltip
                      enableDarkTheme={enableDarkTheme}
                      preferredTimeDisplayMode={preferredTimeDisplayMode}
                      timezone={timezone}
                    />
                  }
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="pressure"
                  stroke={PRESSURE_COLOR}
                  strokeWidth={2}
                  fill={PRESSURE_COLOR}
                  fillOpacity={0.3}
                  name="Pressure"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="forecast"
                  stroke={FORECAST_COLOR}
                  strokeWidth={2}
                  fill={FORECAST_COLOR}
                  fillOpacity={0.3}
                  name="Forecasted"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
};
